import { type FC } from 'react';

import {
  Dropdown,
  DropdownList,
  DropdownListItem,
  DropdownMoreButton,
} from '@wk/components-react16';

import type { CheckpointSystemStatus } from '@/types';

import type {
  CheckpointRow,
  DateEditKind,
} from './CheckpointSettingsPage.types';
import { isTransitionAllowed } from './CheckpointSettingsPage.utils';

interface CheckpointSettingsActionsProps {
  row: CheckpointRow | null | undefined;
  context: 'grid' | 'card';
  canManageCheckpoints: boolean;
  isMobileViewport: boolean;
  onOpenMoveModal: (
    row: CheckpointRow,
    targetStatus: CheckpointSystemStatus,
  ) => void;
  onOpenEditDateModal: (row: CheckpointRow, kind: DateEditKind) => void;
}

const CheckpointSettingsActions: FC<CheckpointSettingsActionsProps> = ({
  row,
  context,
  canManageCheckpoints,
  isMobileViewport,
  onOpenMoveModal,
  onOpenEditDateModal,
}) => {
  if (!canManageCheckpoints || !row) return null;

  const rowKey = `${row.id}-${row.version ?? ''}-${context}`.replace(
    /[^a-zA-Z0-9_-]/g,
    '-',
  );

  return (
    <div className="checkpoints-actions-dropdown">
      <DropdownMoreButton mode="blue" buttonId={`checkpoint-actions-${rowKey}`} />
      <Dropdown
        targetSelector={`#checkpoint-actions-${rowKey}`}
        menuMode="application"
        srLabelledby={`checkpoint-actions-${rowKey}`}
        fullscreenBreakpoint={isMobileViewport ? 'S' : 'XS'}
        dropdownWidth={isMobileViewport ? undefined : '18rem'}
        placementOptions={
          isMobileViewport
            ? undefined
            : {
                positions: [
                  'bottom-left',
                  'top-left',
                  'bottom-right',
                  'top-right',
                ],
              }
        }
      >
        <DropdownList menuMode="application">
          {row.status === 'PREVIEW' ? (
            <DropdownListItem
              onItemClick={() => onOpenEditDateModal(row, 'PREVIEW_END')}
              slot="dropdownListItem"
            >
              Change Out of Preview Date
            </DropdownListItem>
          ) : null}

          {row.status === 'MAINTENANCE' ? (
            <DropdownListItem
              onItemClick={() => onOpenEditDateModal(row, 'MAINTENANCE_DISABLE')}
              slot="dropdownListItem"
            >
              Change Disable Date
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status as CheckpointSystemStatus, 'LIVE') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'LIVE')}
              slot="dropdownListItem"
            >
              Move to Live
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(row.status as CheckpointSystemStatus, 'PREVIEW') ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'PREVIEW')}
              slot="dropdownListItem"
            >
              Move to Preview
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(
            row.status as CheckpointSystemStatus,
            'MAINTENANCE',
          ) ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'MAINTENANCE')}
              slot="dropdownListItem"
            >
              Move to Maintenance
            </DropdownListItem>
          ) : null}

          {isTransitionAllowed(
            row.status as CheckpointSystemStatus,
            'DISABLED',
          ) ? (
            <DropdownListItem
              onItemClick={() => onOpenMoveModal(row, 'DISABLED')}
              slot="dropdownListItem"
            >
              Disable
            </DropdownListItem>
          ) : null}
        </DropdownList>
      </Dropdown>
    </div>
  );
};

export default CheckpointSettingsActions;
